import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import {
  ActionIcon,
  Icon,
  PlainButton,
  inkA,
  orange,
  tokens,
  useIsMobile,
} from 'components/v3';
import useAdminRouteContext from 'hooks/useAdminRouteContext';
import { HelpButton } from '../help';
import {
  formBuilderPath,
  formsListPath,
  formSubmissionsPath,
} from '../shared/routes';
import { nextStatus, StatusSwitch, statusStyle } from './FormStatus';
import LiveConfirmSheet from './LiveConfirmSheet';

// Header shared by the builder (A2) and submissions (A3) pages.
//
// Props: { form, activeTab: 'build' | 'submissions', dirty, statusBusy,
//          onSetStatus(nextStatus), onPreview?, onOpenSettings?,
//          showSettingsButton? }
//
// Desktop: breadcrumb "Forms / <Account> / <Name>", Build | Submissions
// tabs (Submissions carries the entry count), the Live/Draft switch, and
// Preview / Settings when their handlers are passed (Settings also needs
// showSettingsButton — the builder's narrow mode).
//
// Phone (<768): sticky under the 60px app bar. "← Forms", then on the Build
// tab the status chip (opens LiveConfirmSheet), Preview and Settings; on
// the Submissions tab the form name. Build | Submissions tabs below.
//
// Every link is built from routes.js with the viewer's route prefix. The
// status control is disabled while the form is archived or `statusBusy`.
// Help & tours sits last on the right (an icon; on phones a 44px square),
// and only shows where the page mounts FormsHelp.

const Tab = ({ to, active, icon, label, count, fill, help }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <Link
      to={to}
      data-help={help}
      aria-current={active ? 'page' : undefined}
      style={{
        flex: fill ? 1 : 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        minHeight: fill ? 40 : 34,
        padding: '0 14px',
        borderRadius: 7,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        background: active ? t.primary : 'transparent',
        color: active ? 'white' : t.sub,
      }}
    >
      <Icon name={icon} size={15} color="currentColor" />
      {label}
      {count != null ? (
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '1px 7px',
            borderRadius: 999,
            fontVariantNumeric: 'tabular-nums',
            background: active
              ? 'rgba(255, 255, 255, 0.22)'
              : inkA(theme, 0.08),
            color: active ? 'white' : t.sub,
          }}
        >
          {Number(count).toLocaleString()}
        </span>
      ) : null}
    </Link>
  );
};

Tab.propTypes = {
  to: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number,
  fill: PropTypes.bool,
  help: PropTypes.string.isRequired,
};

Tab.defaultProps = { count: null, fill: false };

const Tabs = ({ form, activeTab, prefix, fill }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <nav
      aria-label="Form views"
      data-tour="builder-tabs"
      style={{
        display: 'flex',
        gap: 3,
        padding: 3,
        borderRadius: 10,
        border: '1px solid ' + t.border,
        background: inkA(theme, 0.04),
        width: fill ? '100%' : 'auto',
        boxSizing: 'border-box',
      }}
    >
      <Tab
        to={formBuilderPath(prefix, form.id)}
        active={activeTab === 'build'}
        icon="layout"
        label="Build"
        fill={fill}
        help="builder-tab-build"
      />
      <Tab
        to={formSubmissionsPath(prefix, form.id)}
        active={activeTab === 'submissions'}
        icon="inbox"
        label="Submissions"
        count={form.submission_count || null}
        fill={fill}
        help="builder-tab-submissions"
      />
    </nav>
  );
};

Tabs.propTypes = {
  form: PropTypes.object.isRequired,
  activeTab: PropTypes.oneOf(['build', 'submissions']).isRequired,
  prefix: PropTypes.string.isRequired,
  fill: PropTypes.bool,
};

Tabs.defaultProps = { fill: false };

const Crumb = styled(Link)`
  color: ${(p) => p.$color};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: ${orange(1)};
  }
`;

const DesktopHeader = ({
  form,
  activeTab,
  prefix,
  statusDisabled,
  onSetStatus,
  onPreview,
  settingsButton,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const s = statusStyle(form.status);
  return (
    <header
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px 16px',
        padding: '12px 16px 12px 20px',
        marginBottom: 16,
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
      }}
    >
      <nav
        aria-label="Breadcrumb"
        style={{
          flex: '1 1 240px',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 14,
          color: t.sub,
        }}
      >
        <Crumb to={formsListPath(prefix)} $color={t.sub}>
          Forms
        </Crumb>
        <span aria-hidden="true" style={{ opacity: 0.5 }}>
          /
        </span>
        <Crumb
          to={formsListPath(prefix, { account: form.account.id })}
          $color={t.sub}
          style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {form.account.name}
        </Crumb>
        <span aria-hidden="true" style={{ opacity: 0.5 }}>
          /
        </span>
        <span
          aria-current="page"
          style={{
            color: t.text,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {form.name}
        </span>
      </nav>
      <Tabs form={form} activeTab={activeTab} prefix={prefix} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginLeft: 'auto',
        }}
      >
        <div
          data-tour="builder-status"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '5px 6px 5px 14px',
            borderRadius: 999,
            border: '1px solid ' + t.border,
            background: inkA(theme, 0.03),
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: form.status === 'live' ? s.color : t.faint,
            }}
          >
            {s.label}
          </span>
          <StatusSwitch
            status={form.status}
            size="sm"
            label="Live"
            disabled={statusDisabled}
            onChange={onSetStatus}
          />
        </div>
        {onPreview ? (
          <ButtonV2
            data-tour="builder-preview"
            onClick={onPreview}
            icon={<Icon name="smartphone" size={16} color="currentColor" />}
          >
            Preview
          </ButtonV2>
        ) : null}
        {settingsButton}
        <HelpButton compact />
      </div>
    </header>
  );
};

DesktopHeader.propTypes = {
  form: PropTypes.object.isRequired,
  activeTab: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  statusDisabled: PropTypes.bool.isRequired,
  onSetStatus: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  settingsButton: PropTypes.node,
};

DesktopHeader.defaultProps = { onPreview: undefined, settingsButton: null };

const HeadButton = styled(PlainButton)`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

const StatusChip = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 44px;
  padding: 0 10px 0 14px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;

  &:disabled {
    opacity: 0.5;
  }
`;

const MobileHeader = ({
  form,
  activeTab,
  prefix,
  dirty,
  statusDisabled,
  onSetStatus,
  onPreview,
  onOpenSettings,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  // Below 861px the app's NavMobileLayout pins a 60px bar to the top.
  const hasTopBar = useIsMobile(861);
  const [sheetOpen, setSheetOpen] = useState(false);
  const isLive = form.status === 'live';
  const s = statusStyle(form.status);
  const iconBtn = {
    $border: t.border,
    $bg: inkA(theme, 0.04),
    $color: t.sub,
  };

  return (
    <>
      <header
        style={{
          position: 'sticky',
          top: hasTopBar ? 60 : 0,
          zIndex: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          margin: '-16px -12px 12px',
          padding: '8px 16px 12px',
          background: t.card,
          borderBottom: '1px solid ' + t.border,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 44,
          }}
        >
          <Link
            to={formsListPath(prefix)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              minHeight: 44,
              marginLeft: -6,
              padding: '0 10px 0 2px',
              color: t.primary,
              fontSize: 16,
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            <Icon name="chevron-left" size={18} color="currentColor" />
            Forms
          </Link>
          {activeTab === 'build' ? (
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <StatusChip
                data-tour="builder-status"
                aria-label={`Form status: ${s.label}`}
                aria-haspopup="dialog"
                disabled={statusDisabled}
                onClick={() => setSheetOpen(true)}
                $border={isLive ? s.border : t.border}
                $bg={isLive ? s.background : inkA(theme, 0.04)}
                $color={isLive ? s.color : t.faint}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'currentColor',
                  }}
                />
                {s.label}
                <Icon name="chevron-down" size={14} color="currentColor" />
              </StatusChip>
              {onPreview ? (
                <HeadButton
                  data-tour="builder-preview"
                  aria-label="Preview form"
                  onClick={onPreview}
                  {...iconBtn}
                >
                  <Icon name="eye" size={19} color="currentColor" />
                </HeadButton>
              ) : null}
              {onOpenSettings ? (
                <HeadButton
                  aria-label="Form settings"
                  onClick={onOpenSettings}
                  {...iconBtn}
                >
                  <Icon name="settings" size={19} color="currentColor" />
                </HeadButton>
              ) : null}
              <HelpButton compact size={44} muted />
            </div>
          ) : (
            <div style={{ marginLeft: 'auto' }}>
              <HelpButton compact size={44} muted />
            </div>
          )}
        </div>
        {activeTab === 'submissions' ? (
          <div
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: t.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {form.name}
          </div>
        ) : null}
        <Tabs form={form} activeTab={activeTab} prefix={prefix} fill />
      </header>
      <LiveConfirmSheet
        open={sheetOpen}
        status={form.status}
        dirty={dirty}
        onClose={() => setSheetOpen(false)}
        onConfirm={(next) => {
          setSheetOpen(false);
          onSetStatus(next);
        }}
      />
    </>
  );
};

MobileHeader.propTypes = {
  form: PropTypes.object.isRequired,
  activeTab: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  dirty: PropTypes.bool.isRequired,
  statusDisabled: PropTypes.bool.isRequired,
  onSetStatus: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  onOpenSettings: PropTypes.func,
};

MobileHeader.defaultProps = { onPreview: undefined, onOpenSettings: undefined };

const FormShellHeader = ({
  form,
  activeTab,
  dirty,
  statusBusy,
  onSetStatus,
  onPreview,
  onOpenSettings,
  showSettingsButton,
}) => {
  const isMobile = useIsMobile();
  const { routePrefix } = useAdminRouteContext();
  const statusDisabled = !!form.archived_at || !!statusBusy;

  if (isMobile) {
    return (
      <MobileHeader
        form={form}
        activeTab={activeTab}
        prefix={routePrefix}
        dirty={!!dirty}
        statusDisabled={statusDisabled}
        onSetStatus={onSetStatus}
        onPreview={onPreview}
        onOpenSettings={onOpenSettings}
      />
    );
  }

  return (
    <DesktopHeader
      form={form}
      activeTab={activeTab}
      prefix={routePrefix}
      statusDisabled={statusDisabled}
      onSetStatus={onSetStatus}
      onPreview={onPreview}
      settingsButton={
        showSettingsButton && onOpenSettings ? (
          <ActionIcon
            icon="settings"
            title="Form settings"
            onClick={onOpenSettings}
          />
        ) : null
      }
    />
  );
};

FormShellHeader.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['live', 'draft']).isRequired,
    archived_at: PropTypes.string,
    submission_count: PropTypes.number,
    account: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  activeTab: PropTypes.oneOf(['build', 'submissions']).isRequired,
  dirty: PropTypes.bool,
  statusBusy: PropTypes.bool,
  onSetStatus: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  onOpenSettings: PropTypes.func,
  showSettingsButton: PropTypes.bool,
};

FormShellHeader.defaultProps = {
  dirty: false,
  statusBusy: false,
  onPreview: undefined,
  onOpenSettings: undefined,
  showSettingsButton: false,
};

export { nextStatus };
export default FormShellHeader;
